import moment from 'moment';
import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { ACalendar, Input, Label, Select } from 'components/atoms';
import { CheckBox } from 'components/atoms/CheckBox/CheckBox.components';
import { Section } from 'components/molecules/TabPanel/TabPanel';
import { useGetPicklistQuery } from 'services/sap';
import { ReactComponent as WarningIcon } from 'utils/assets/images/warningIcon.svg';
import { catalogoPersonal, catalogoLenguage } from 'utils/helpers/dictionaries';
import { formatValueForDisplay, removeDots } from 'utils/helpers/formatValueForDisplay';
import { formTypes, UserProfileStatus } from 'utils/interfaces';

import { useProfileFormContext } from '../hooks/useProfileFormContext';

import './TabExperience.component.scss';

interface ITabExperienceProps {
  isDisabled: boolean;
  changeData: (value: { name: formTypes; value?: number | string | boolean }) => Promise<void>;
  setMarginForCalendar: (state: number) => void;
  profileProgress?: UserProfileStatus | null;
}

export const TabExperience = ({
  isDisabled,
  changeData,
  setMarginForCalendar,
  profileProgress,
}: ITabExperienceProps) => {
  const {
    register,
    trigger,
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const { profileData, formValues, isErrorProfileSubmiting } = useProfileFormContext();

  const [noExperience, setNoExperience] = useState(true);
  const [isOpenStartDateCalendar, setIsOpenStartDateCalendar] = useState(false);
  const [isOpenEndDateCalendar, setIsOpenEndDateCalendar] = useState(false);

  const { data: coin } = useGetPicklistQuery({
    catalogName: catalogoPersonal.currency,
    languages: catalogoLenguage.ES,
  });

  const hasExperienceFieldValidation = async (newValue: boolean) => {
    setNoExperience(newValue);

    setValue('hasExperience', newValue, { shouldValidate: true });
    changeData({ name: 'hasExperience', value: newValue });

    if (newValue) {
      const fieldsToClear = [
        'jobtitle',
        'company',
        'job_start_date',
        'job_end_date',
        'currentSalaryCurrency',
        'currentSalary',
      ];

      const clearFields = async () => {
        for (const field of fieldsToClear) {
          await changeData({ name: field as formTypes, value: '' });
          setValue(field, '', { shouldValidate: false });
        }
        clearErrors(fieldsToClear);
      };

      await clearFields();
    } else {
      trigger(['jobtitle', 'company', 'job_start_date', 'currentSalaryCurrency', 'currentSalary']);
    }
  };

  const handleNoExperienceChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;

    hasExperienceFieldValidation(newValue);
  };

  useEffect(() => {
    if (profileData && !isErrorProfileSubmiting) {
      let initialNoExperience: boolean;

      if (profileProgress === UserProfileStatus.STARTED) {
        initialNoExperience = false;
      } else {
        initialNoExperience = !profileData.hasExperience;
      }

      setNoExperience(initialNoExperience);
      setValue('hasExperience', initialNoExperience, { shouldValidate: true });

      const fieldsToInitialize = [
        { name: 'jobtitle', formValue: formValues.jobtitle, profileValue: profileData.jobtitle },
        { name: 'company', formValue: formValues.company, profileValue: profileData.company },
        {
          name: 'job_start_date',
          formValue: formValues.job_start_date,
          profileValue: profileData.job_start_date,
        },
        {
          name: 'job_end_date',
          formValue: formValues.job_end_date,
          profileValue: profileData.job_end_date,
        },
        {
          name: 'currentSalaryCurrency',
          formValue: formValues.currentSalaryCurrency,
          profileValue: profileData.currentSalaryCurrency,
        },
        {
          name: 'currentSalary',
          formValue: formValues.currentSalary,
          profileValue: profileData.currentSalary,
        },
      ];

      if (!initialNoExperience) {
        fieldsToInitialize.forEach(({ name, formValue, profileValue }) => {
          if (formValue === '' && profileValue && profileValue !== '') {
            changeData({ name: name as formTypes, value: profileValue });
          }
        });
      } else {
        fieldsToInitialize.forEach(({ name }) => {
          changeData({ name: name as formTypes, value: '' });
        });
      }

      if (
        formValues.expectedSalaryCurrencyDropdown === '' &&
        profileData.expectedSalaryCurrencyDropdown
      ) {
        changeData({
          name: 'expectedSalaryCurrencyDropdown',
          value: profileData.expectedSalaryCurrencyDropdown,
        });
      }

      if (formValues.expectedSalaryNumeric === '' && profileData.expectedSalaryNumeric) {
        changeData({ name: 'expectedSalaryNumeric', value: profileData.expectedSalaryNumeric });
      }
    }
  }, [profileData, isErrorProfileSubmiting]);

  return (
    <Section id={3}>
      <section className='section-form'>
        <aside className='section-form__aside'>
          <article
            className={`section-form__article
              ${noExperience && !isDisabled
                ? 'section-form__article__noexp section-form__article__noexp--disabled'
                : ''
              }`}
          >
            <Input
              id='jobtitle'
              label='Cargo (último / actual)'
              placeholder='Ej: Arquitecto'
              value={noExperience ? '' : formValues.jobtitle}
              onChange={(e) => {
                const { value } = e.currentTarget;
                changeData({ name: 'jobtitle', value });
              }}
              disabled={isDisabled || noExperience}
              register={register}
              error={!!errors.jobtitle && !noExperience}
            />
          </article>
          <article
            className={`section-form__article
              ${noExperience && !isDisabled
                ? 'section-form__article__noexp section-form__article__noexp--disabled'
                : ''
              }`}
          >
            <Input
              id='company'
              label='Empresa u organización'
              placeholder='Ej: Pirani'
              value={noExperience ? '' : formValues.company}
              onChange={(e) => {
                const { value } = e.currentTarget;
                changeData({ name: 'company', value });
              }}
              disabled={isDisabled || noExperience}
              register={register}
              error={!!errors.company && !noExperience}
            />
          </article>
        </aside>

        <aside className='section-form__aside'>
          <article
            className={`section-form__article
              ${noExperience && !isDisabled ? 'section-form__article__noexp' : ''}`}
          >
            {isDisabled ? (
              <Input
                id='job_start_date'
                label='Fecha de inicio'
                placeholder='Selecciona'
                type='text'
                value={noExperience ? '' : formValues.job_start_date}
                disabled
              />
            ) : (
              <>
                <Label text={'Fecha de inicio'} htmlFor='job_start_date' />
                <ACalendar
                  birthdateForm={
                    formValues.job_start_date ? moment(formValues.job_start_date) : undefined
                  }
                  updateBirthdate={(date) => changeData({ name: 'job_start_date', value: date })}
                  disabled={isDisabled || noExperience}
                  setMarginForCalendar={setMarginForCalendar}
                  isOpenCalendar={isOpenStartDateCalendar}
                  setIsOpenCalendar={setIsOpenStartDateCalendar}
                  error={!!errors.job_start_date && !noExperience}
                />
                {errors.job_start_date && !noExperience && (
                  <div className='subtext-error'>
                    <WarningIcon />
                    <span>Campo obligatorio</span>
                  </div>
                )}
              </>
            )}
          </article>
          <article
            className={`section-form__article
              ${noExperience && !isDisabled ? 'section-form__article__noexp' : ''}`}
          >
            {isDisabled ? (
              <Input
                id='job_end_date'
                label='Fecha de finalización (opcional) '
                placeholder='Selecciona'
                type='text'
                value={noExperience ? '' : formValues.job_end_date}
                disabled
              />
            ) : (
              <>
                <Label text={'Fecha de finalización (opcional) '} htmlFor='job_end_date' />
                <ACalendar
                  birthdateForm={
                    formValues.job_end_date ? moment(formValues.job_end_date) : undefined
                  }
                  updateBirthdate={(date) => changeData({ name: 'job_end_date', value: date })}
                  disabled={isDisabled || noExperience}
                  setMarginForCalendar={setMarginForCalendar}
                  isOpenCalendar={isOpenEndDateCalendar}
                  setIsOpenCalendar={setIsOpenEndDateCalendar}
                  error={!!errors.job_end_date && !noExperience}
                />
                {errors.job_end_date && !noExperience && (
                  <div className='subtext-error'>
                    <WarningIcon />
                    <span>Campo obligatorio</span>
                  </div>
                )}
              </>
            )}
          </article>
        </aside>

        <aside className='section-form__aside  section-form__aside--coin'>
          <article className='section-form__article section-form__coin'>
            {isDisabled ? (
              <Input
                id='currentSalaryCurrency'
                label='Moneda'
                placeholder='Selecciona'
                type='text'
                register={register}
                value={noExperience ? '' : formValues.currentSalaryCurrency}
                disabled
              />
            ) : (
              <Select
                placeholder='Selecciona'
                htmlForLabel={'currentSalaryCurrency'}
                textLabel='Moneda'
                options={
                  coin?.map((item: any) => ({
                    value: item.label_es_ES,
                    label: item.label_es_ES,
                  })) ?? []
                }
                selected={formValues?.currentSalaryCurrency}
                disabled={isDisabled || noExperience}
                handleChange={(value) => {
                  changeData({
                    name: 'currentSalaryCurrency',
                    value,
                  });
                }}
                error={!!errors.currentSalaryCurrency && !noExperience}
              />
            )}
          </article>
          <article
            className={`section-form__article
              ${noExperience && !isDisabled
                ? 'section-form__article__noexp section-form__article__noexp--disabled'
                : ''
              }`}
          >
            <Controller
              control={control}
              name='currentSalary'
              render={({ field: { value } }) => (
                <Input
                  onChange={(e) => {
                    const { value: inputValue } = e.currentTarget;

                    const valueWithoutDots = removeDots(inputValue);
                    changeData({
                      name: 'currentSalary',
                      value: valueWithoutDots,
                    });
                  }}
                  disabled={isDisabled || noExperience}
                  register={register}
                  id='currentSalary'
                  placeholder='Monto'
                  label='Salario'
                  value={value !== undefined ? formatValueForDisplay(value) : ''}
                  error={!!errors.currentSalary && !noExperience}
                />
              )}
            />
          </article>
        </aside>
        <label className='aside__label'>
          <Controller
            control={control}
            name='hasExperience'
            render={({ field: { value } }) => (
              <>
                <CheckBox
                  name='hasExperience'
                  checked={value}
                  onChange={handleNoExperienceChange}
                  disabled={isDisabled}
                  className={value ? 'checked' : ''}
                />
                No tengo experiencia
              </>
            )}
          />
        </label>
        <p className='section__aspiration'>¿Cuál es tu aspiración salarial? </p>
        <aside className='section-form__aside section-form__aside--coinAspiration'>
          <div className='section-form__article section-form__coinAspiration'>
            {isDisabled ? (
              <Input
                id='expectedSalaryCurrencyDropdown'
                label='Moneda'
                placeholder='Selecciona'
                type='text'
                register={register}
                value={formValues.expectedSalaryCurrencyDropdown}
                disabled
              />
            ) : (
              <Select
                placeholder='Selecciona'
                htmlForLabel={'expectedSalaryCurrencyDropdown'}
                textLabel='Moneda'
                options={
                  coin?.map((item: any) => ({
                    value: item.label_es_ES,
                    label: item.label_es_ES,
                  })) ?? []
                }
                selected={formValues?.expectedSalaryCurrencyDropdown}
                disabled={isDisabled}
                handleChange={(value) => {
                  changeData({
                    name: 'expectedSalaryCurrencyDropdown',
                    value,
                  });
                }}
                error={!!errors.expectedSalaryCurrencyDropdown}
              />
            )}
          </div>
          <div className='section-form__article'>
            <Controller
              control={control}
              name='expectedSalaryNumeric'
              render={({ field: { value } }) => (
                <Input
                  onChange={(e) => {
                    const { value: inputValue } = e.currentTarget;

                    const valueWithoutDots = removeDots(inputValue);
                    changeData({
                      name: 'expectedSalaryNumeric',
                      value: valueWithoutDots,
                    });
                  }}
                  disabled={isDisabled}
                  register={register}
                  id='expectedSalaryNumeric'
                  placeholder='Monto'
                  label='Salario'
                  value={value !== undefined ? formatValueForDisplay(value) : ''}
                  error={!!errors.expectedSalaryNumeric}
                />
              )}
            />
          </div>
        </aside>
      </section>
    </Section>
  );
};
